/**
 * Per-tab browser-back integration for the workspace tabs system.
 *
 * Goal: when the user presses the browser Back/Forward button, navigation
 * stays inside the currently active tab instead of jumping across tabs.
 *
 * Strategy:
 *   1. A capture-phase popstate listener records the popped wsTab stamp.
 *      This file MUST be imported before Vue Router is created so the
 *      listener registers ahead of Vue Router's own popstate listener.
 *   2. router.afterEach stamps each browser history entry with its
 *      owning tabKey via history.replaceState. Vue Router's built-in
 *      `state.position` field is used as the monotonic counter for
 *      direction detection — no parallel serial we have to maintain.
 *   3. router.beforeEach reads the pending stamp:
 *      - intra-tab back/forward → mirror tab.idx, allow Vue Router nav.
 *      - cross-tab pop → either redirect to active tab's previous local
 *        entry, or cancel + restore URL via history.forward/back.
 *   4. tab.idx mutation for redirected cross-tab pops is DEFERRED until
 *      router.afterEach confirms the redirected nav succeeded — avoids
 *      desync if a downstream guard rejects.
 *
 * The active tab's per-tab history stack lives on the tab object in the
 * tabs Vuex module: `tab.history[]` + `tab.idx`. PUSH_TAB_HISTORY,
 * MOVE_TAB_IDX and SET_TAB_IDX mutations manage it.
 */

const STATE_KEY = '__wsTab';
const HMR_LISTENER_KEY = '__tabHistoryPopstateListener';

// pendingPopstateStamp + currentPosition + pendingRedirect live on `window` so
// they survive Vue HMR. The module-level vars below mirror them for ergonomic
// access, and stay in sync via the listener below.
let pendingPopstateStamp = null;
let pendingRedirect = null;
let currentPosition = 0;

// HMR-safe popstate listener registration: remove the previous listener
// (if any) and register a fresh one. Using window-scoped key so the
// reference survives across module reloads.
if (typeof window !== 'undefined') {
  if (window[HMR_LISTENER_KEY]) {
    window.removeEventListener('popstate', window[HMR_LISTENER_KEY], true);
  }
  const listener = (event) => {
    const state = event.state || {};
    const stamp = state[STATE_KEY] || null;
    const popPosition = typeof state.position === 'number' ? state.position : 0;
    const direction = popPosition < currentPosition ? 'back' : 'forward';
    pendingPopstateStamp = stamp ? { ...stamp, direction } : null;
  };
  window[HMR_LISTENER_KEY] = listener;
  window.addEventListener('popstate', listener, true);
}

function getActiveTab(store) {
  const key = store.state.tabs?.activeKey;
  if (!key) return null;
  return store.state.tabs.openTabs.find((t) => t.key === key) || null;
}

export function installTabHistory(router, store) {
  router.afterEach((to, from, failure) => {
    // Clear pending redirect regardless of outcome — a failure must not
    // leave a stale commit queued for the next nav.
    const redirect = pendingRedirect;
    pendingRedirect = null;
    // Also drop any stamp that beforeEach didn't read (defensive: a guard
    // earlier in the chain may have short-circuited the navigation).
    pendingPopstateStamp = null;

    if (failure) return;

    // Apply deferred tab.idx mutation only after a successful nav.
    if (redirect) {
      store.commit('tabs/MOVE_TAB_IDX', redirect);
    }

    const tabKey = store.state.tabs?.activeKey;
    if (!tabKey) return;

    const stamp = {
      ...(window.history.state || {}),
      [STATE_KEY]: { tabKey, path: to.fullPath },
    };
    try {
      window.history.replaceState(stamp, '', to.fullPath);
    } catch (err) {
      // Some browsers throttle replaceState — surface for debuggability.
      console.warn('[tabHistory] replaceState failed:', err);
    }

    // Cache Vue Router's built-in monotonic position for direction
    // detection on the next popstate.
    const pos = window.history.state?.position;
    if (typeof pos === 'number') currentPosition = pos;
  });

  router.beforeEach((to, from, next) => {
    const stamp = pendingPopstateStamp;
    pendingPopstateStamp = null;

    if (!stamp) return next(); // programmatic nav, not popstate-driven

    const activeKey = store.state.tabs?.activeKey;
    if (!activeKey) return next();

    // Intra-tab back/forward — Vue Router handles. Mirror new index.
    if (stamp.tabKey === activeKey) {
      const tab = getActiveTab(store);
      if (tab && Array.isArray(tab.history)) {
        const idx = tab.history.indexOf(stamp.path);
        if (idx >= 0) store.commit('tabs/SET_TAB_IDX', { key: activeKey, idx });
      }
      return next();
    }

    // Cross-tab popstate — step within active tab's local stack instead.
    const tab = getActiveTab(store);
    if (!tab || !Array.isArray(tab.history) || tab.history.length === 0) {
      cancelAndRestore(stamp.direction);
      return next(false);
    }

    const delta = stamp.direction === 'back' ? -1 : 1;
    const cur = typeof tab.idx === 'number' ? tab.idx : tab.history.length - 1;
    const newIdx = cur + delta;

    if (newIdx >= 0 && newIdx < tab.history.length) {
      // Defer the commit until afterEach confirms the redirected nav
      // resolved successfully.
      pendingRedirect = { key: activeKey, delta };
      next(tab.history[newIdx]);
      return;
    }

    // Active tab can't step further — cancel and explicitly restore URL.
    cancelAndRestore(stamp.direction);
    next(false);
  });
}

/**
 * Explicitly undo a popstate-driven URL change. Vue Router 4 *usually*
 * restores the URL on cancelled popstate navs but it is not contractually
 * guaranteed across patches/browsers. Call history.forward/back to make it
 * explicit.
 */
function cancelAndRestore(direction) {
  try {
    if (direction === 'back') window.history.forward();
    else window.history.back();
  } catch (err) {
    console.warn('[tabHistory] history restore failed:', err);
  }
}
